import { FormEvent, ReactNode, useMemo, useState } from 'react';
import { Input, Select, Checkbox, Button, SearchableSelect } from '../atoms';
import { formatPhone } from '../lib/formatPhone';

/**
 * Above this many options an FK picker becomes a typeahead instead of a native
 * <select> (§1.6 — "searchable client and property dropdowns"). A firm's client
 * and property lists run to hundreds; scrolling a native select past a screenful
 * to find one is the complaint. Below it the native control is kept: it is
 * better on mobile and a short list needs no filtering.
 *
 * A length rule rather than a per-field declaration, deliberately. Every field on
 * this path comes from `GET /api/schema/:table` — there is no human-authored
 * field registry to hang a flag on, so a declaration would mean a second list to
 * maintain by hand and to forget. The rule maintains itself: the moment a firm's
 * client list outgrows a screen, the picker becomes searchable everywhere it is
 * shown, with nothing to remember.
 */
export const FK_SEARCHABLE_THRESHOLD = 10;

/**
 * Form — schema-driven form renderer.
 *
 * Derived from M3 Text Fields + Forms spec per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/text-fields/specs
 *                (M3 has no canonical "Form" component; the pattern is
 *                composed from labeled Text Fields + grouping headers)
 *   - Reference: Material Web (none for forms; uses individual mdc-* fields)
 *
 * Populus adaptations applied:
 *   - Consumes the `fields` shape returned by `GET /api/schema/:table`
 *     (Phase 11 Block 2 PR 1) — single source of truth for entity shape
 *   - Section grouping per `field.section`; sorted within section by
 *     `field.displayOrder` (M3 doesn't formalize sections; Populus needs
 *     them for 60-table CRUD where each entity has 15-30 fields)
 *   - Conditional show/hide per `field.conditional.showWhen` — re-evaluates
 *     on every value change; hidden fields keep their value in state but
 *     aren't submitted (consumer can filter on submit)
 *   - Field types dispatched by `field.dataType`:
 *       boolean → Checkbox atom (with label on right)
 *       integer/number → Input type=number
 *       date → Input type=date
 *       timestamp → Input type=datetime-local
 *       text with enumOptions → Select atom
 *       text otherwise → Input type=text
 *   - FK fields: if consumer passes `fkOptions[fieldName]`, render as
 *     Select; otherwise render as plain text input with a helper line
 *     ("FK to <table>"). Form doesn't fetch FK options itself
 *     (decoupling from data fetching — consumer can wire TanStack Query
 *     and pass a hash of options)
 *   - `immutable: true` + `mode='edit'` disables the field
 *   - Submit button row sticky at the bottom right (M3 Dialog footer
 *     pattern; matches the entity-form interaction shape)
 *
 * Token discipline:
 *   - Section headers, helper text, error text → --color-ink* (system)
 *   - Required-field asterisk → --color-status-red (semantic system token)
 *   - Submit button inherits Button atom --brand-primary
 *
 * Phase 11 Block 6 PR 6 (2026-05-12).
 */

// A `showWhen` clause matches either a single value (scalar `!==`) or any value in
// an array (`.includes`). The backend metadata contract documents both shapes
// (`<required_value_or_array>` in src/config/schema-metadata.js).
type ShowWhenScalar = string | number | boolean;
export type ShowWhenValue = ShowWhenScalar | ShowWhenScalar[];

export interface SchemaField {
  name: string;
  type: string;                                  // raw PG type (preserved for backwards compat)
  required: boolean;
  // Parsed literal from the schema endpoint (not the raw PG default expression):
  // 'US'::text → "US", a BOOLEAN default → boolean, nextval(...)/now() → null.
  defaultValue: string | boolean | null;
  pk: boolean;
  enum: string[] | null;
  fk: { table: string; column: string } | null;
  label: string;
  section: string | null;
  displayOrder: number;
  immutable: boolean;
  hidden: boolean;
  /** Hidden on the CREATE form only — the detail page still offers it (schema-metadata CREATE_HIDDEN_FIELDS). */
  createHidden?: boolean;
  conditional: { showWhen: Record<string, ShowWhenValue> } | null;
  enumOptions: Array<{ value: string; label: string }> | null;
  dataType: 'boolean' | 'integer' | 'number' | 'date' | 'timestamp' | 'text';
}

export type FormValues = Record<string, unknown>;

export interface FormProps {
  fields: SchemaField[];
  initialValues?: FormValues;
  mode?: 'create' | 'edit';
  fkOptions?: Record<string, Array<{ value: string; label: string }>>;
  /** FK fields rendered as a typeahead regardless of option count (a people picker
   *  should filter by typing even when the firm is five people). */
  searchableFields?: string[];
  /** Fields the consumer hides for this render (an org-level toggle the schema cannot know). */
  hideFields?: string[];
  errors?: Record<string, string>;
  submitLabel?: string;
  onSubmit: (values: FormValues) => void | Promise<void>;
  onCancel?: () => void;
  submitting?: boolean;
  // Optional advisory slot rendered between the fields and the actions, given the
  // form's live values (e.g. a "possible duplicate" banner on parcel create).
  renderBanner?: (values: FormValues) => ReactNode;
}

export function Form({
  fields,
  initialValues = {},
  mode = 'create',
  fkOptions = {},
  searchableFields = [],
  hideFields = [],
  errors = {},
  submitLabel,
  onSubmit,
  onCancel,
  submitting = false,
  renderBanner,
}: FormProps) {
  const visibleFields = useMemo(
    () => fields.filter(f => !f.hidden && !f.pk && !hideFields.includes(f.name) && !(mode === 'create' && f.createHidden)),
    [fields, hideFields, mode],
  );
  const searchable = useMemo(() => new Set(searchableFields), [searchableFields]);

  // Group by section, preserving section order from displayOrder of the
  // first field in each section
  const sections = useMemo(() => {
    const sectionMap = new Map<string, SchemaField[]>();
    for (const f of visibleFields) {
      const key = f.section ?? '_unsectioned';
      if (!sectionMap.has(key)) sectionMap.set(key, []);
      sectionMap.get(key)!.push(f);
    }
    for (const [, fs] of sectionMap) {
      fs.sort((a, b) => a.displayOrder - b.displayOrder);
    }
    return Array.from(sectionMap.entries()).map(([label, fields]) => ({
      label: label === '_unsectioned' ? null : label,
      fields,
      sortKey: fields[0]?.displayOrder ?? 0,
    })).sort((a, b) => a.sortKey - b.sortKey);
  }, [visibleFields]);

  const [values, setValues] = useState<FormValues>(() => {
    const next: FormValues = { ...initialValues };
    for (const f of fields) {
      if (next[f.name] === undefined && f.defaultValue !== null) {
        next[f.name] = f.defaultValue;
      }
    }
    return next;
  });

  function setValue(name: string, value: unknown) {
    setValues(prev => ({ ...prev, [name]: value }));
  }

  function isVisible(field: SchemaField): boolean {
    if (!field.conditional) return true;
    for (const [depField, expected] of Object.entries(field.conditional.showWhen)) {
      const actual = values[depField] as ShowWhenScalar;
      // Array → the field shows when the dependency holds ANY of the listed values;
      // scalar → exact match. (One field can bill on a rate for several billing_types.)
      const matches = Array.isArray(expected)
        ? expected.includes(actual)
        : actual === expected;
      if (!matches) return false;
    }
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    await onSubmit(values);
  }

  const submitText = submitLabel ?? (mode === 'edit' ? 'Save changes' : 'Create');

  return (
    <form onSubmit={handleSubmit}>
      {sections.map((section, i) => (
        <FormSectionView
          key={i}
          searchable={searchable}
          label={section.label}
          fields={section.fields.filter(isVisible)}
          values={values}
          errors={errors}
          mode={mode}
          fkOptions={fkOptions}
          setValue={setValue}
        />
      ))}

      {renderBanner?.(values)}

      <footer style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        marginTop: 'var(--sp-6)',
        paddingTop: 'var(--sp-4)',
        borderTop: '1px solid var(--color-border)',
      }}>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" loading={submitting} disabled={submitting}>
          {submitText}
        </Button>
      </footer>
    </form>
  );
}

interface FormSectionViewProps {
  label: string | null;
  fields: SchemaField[];
  values: FormValues;
  errors: Record<string, string>;
  mode: 'create' | 'edit';
  fkOptions: Record<string, Array<{ value: string; label: string }>>;
  searchable: Set<string>;
  setValue: (name: string, value: unknown) => void;
}

function FormSectionView({
  label,
  fields,
  values,
  errors,
  mode,
  fkOptions,
  searchable,
  setValue,
}: FormSectionViewProps) {
  if (fields.length === 0) return null;
  return (
    <section style={{ marginBottom: 'var(--sp-5)' }}>
      {label && (
        <h3 style={{
          margin: '0 0 var(--sp-3)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>{label}</h3>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: 'var(--sp-4)',
      }}>
        {fields.map(field => (
          <FieldView
            key={field.name}
            field={field}
            value={values[field.name]}
            error={errors[field.name]}
            disabled={mode === 'edit' && field.immutable}
            fkOptions={fkOptions[field.name]}
            searchable={searchable.has(field.name)}
            onChange={v => setValue(field.name, v)}
          />
        ))}
      </div>
    </section>
  );
}

interface FieldViewProps {
  field: SchemaField;
  value: unknown;
  error?: string;
  disabled?: boolean;
  fkOptions?: Array<{ value: string; label: string }>;
  searchable?: boolean;
  onChange: (value: unknown) => void;
}

function FieldView({ field, value, error, disabled, fkOptions, searchable, onChange }: FieldViewProps) {
  const labelNode: ReactNode = (
    <>
      {field.label}
      {field.required && (
        <span aria-hidden="true" style={{
          color: 'var(--color-status-red)',
          marginLeft: 2,
        }}>*</span>
      )}
    </>
  );

  // Checkbox renders its own label on the right; everything else uses
  // the standard label-above pattern
  if (field.dataType === 'boolean') {
    return (
      <div>
        <Checkbox
          checked={!!value}
          onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          label={labelNode}
        />
        {error && <FieldError text={error} />}
      </div>
    );
  }

  return (
    <label style={{ display: 'block' }}>
      <span style={{
        display: 'block',
        marginBottom: 'var(--sp-1)',
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)',
        color: 'var(--color-ink)',
      }}>{labelNode}</span>
      <FieldControl
        field={field}
        value={value}
        disabled={disabled}
        invalid={!!error}
        fkOptions={fkOptions}
        searchable={searchable}
        onChange={onChange}
      />
      {field.fk && !fkOptions && (
        <div style={{
          marginTop: 'var(--sp-0-5)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--color-ink-3)',
        }}>FK: {field.fk.table}</div>
      )}
      {error && <FieldError text={error} />}
    </label>
  );
}

interface FieldControlProps {
  field: SchemaField;
  value: unknown;
  disabled?: boolean;
  invalid?: boolean;
  fkOptions?: Array<{ value: string; label: string }>;
  searchable?: boolean;
  onChange: (value: unknown) => void;
}

function FieldControl({ field, value, disabled, invalid, fkOptions, searchable, onChange }: FieldControlProps) {
  const strVal = value === null || value === undefined ? '' : String(value);

  // FK with provided options → Select, or a typeahead once the list is long
  // enough that scrolling it is the slower way to find a row (§1.6), or whenever the
  // consumer asks for one (a people picker filters by typing at any size).
  if (field.fk && fkOptions) {
    if (searchable || fkOptions.length > FK_SEARCHABLE_THRESHOLD) {
      return (
        <SearchableSelect
          value={strVal || null}
          disabled={disabled}
          invalid={invalid}
          onChange={v => onChange(v)}
          options={fkOptions}
          placeholder={`Search ${field.label.toLowerCase()}…`}
          emptyLabel={field.required ? undefined : `No ${field.label.toLowerCase()}`}
          aria-label={field.label}
        />
      );
    }
    return (
      <Select
        value={strVal}
        disabled={disabled}
        invalid={invalid}
        onChange={e => onChange(e.target.value || null)}
        options={fkOptions}
        placeholder={`Select ${field.label.toLowerCase()}…`}
      />
    );
  }

  // Enum → Select
  if (field.enumOptions && field.enumOptions.length > 0) {
    // A stored value the option list does not know (imported data carrying a legacy
    // label such as "ARB Decision" where the enum says `appealed`) must still be SHOWN:
    // a native <select> whose value matches no <option> renders the placeholder, so the
    // record reads as having no status and the person cannot tell what they would be
    // changing. Keep the current value selectable, labelled as it is stored.
    const enumOptions = strVal !== '' && !field.enumOptions.some(o => String(o.value) === strVal)
      ? [{ value: strVal, label: strVal }, ...field.enumOptions]
      : field.enumOptions;
    return (
      <Select
        value={strVal}
        disabled={disabled}
        invalid={invalid}
        onChange={e => onChange(e.target.value || null)}
        options={enumOptions}
        placeholder={`Select ${field.label.toLowerCase()}…`}
      />
    );
  }

  // Phone → text input locked to the US 3-3-4 cadence (XXX-XXX-XXXX), uniform
  // across every schema-driven form in the app.
  if (/phone/i.test(field.name) && field.dataType === 'text') {
    return (
      <Input
        type="text"
        inputMode="tel"
        placeholder="XXX-XXX-XXXX"
        value={strVal}
        disabled={disabled}
        invalid={invalid}
        required={field.required}
        onChange={e => { const f = formatPhone(e.target.value); onChange(f === '' ? null : f); }}
      />
    );
  }

  // Number, date, timestamp → typed inputs
  let inputType: string = 'text';
  if (field.dataType === 'integer' || field.dataType === 'number') inputType = 'number';
  else if (field.dataType === 'date') inputType = 'date';
  else if (field.dataType === 'timestamp') inputType = 'datetime-local';

  return (
    <Input
      type={inputType}
      value={strVal}
      disabled={disabled}
      invalid={invalid}
      required={field.required}
      onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
    />
  );
}

function FieldError({ text }: { text: string }) {
  return (
    <div style={{
      marginTop: 'var(--sp-1)',
      fontSize: 'var(--fs-sm)',
      color: 'var(--color-status-red)',
    }}>{text}</div>
  );
}
